import { For, Show, createMemo, createSignal } from 'solid-js';
import workHistory from '../work/work.json';
import { A11Y } from '../../../constants/app-constants';
import {
  WorkHead,
  WorkTitle,
  WorkNodeHeading,
  WorkStamp,
  WorkList,
  WorkLinks,
  TreeToggle,
  Tree,
  TrunkNode,
  SideNodes,
  SideNode,
  SideTitle,
} from './styled-field';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * @param {number} stamp
 * @returns {string}
 */
export const formatStamp = (stamp) => {
  const value = String(stamp);
  const year = value.slice(0, 4);
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  return `${MONTHS[month] || '??'} ${day} ${year}`;
};

/**
 * Group milestone nodes onto the posting they sat under.
 * @param {Array<object>} entries
 */
export const buildWorkTree = (entries) => {
  const chronological = [...entries].sort((a, b) => a.stamp - b.stamp);
  const trunks = [];
  let current = null;

  chronological.forEach((entry) => {
    if (entry.type === 'work') {
      current = { ...entry, sides: [] };
      trunks.push(current);
      return;
    }
    if (!current) {
      current = {
        _id: 'pre-career',
        title: 'Before first posting',
        type: 'work',
        stamp: entry.stamp,
        bullets: [],
        sides: [],
      };
      trunks.push(current);
    }
    current.sides.push(entry);
  });

  return trunks.sort((a, b) => {
    const latest = (node) =>
      Math.max(node.stamp, ...(node.sides || []).map((side) => side.stamp), 0);
    return latest(b) - latest(a);
  });
};

const latestEntry = (entries) => [...entries].sort((a, b) => b.stamp - a.stamp)[0];

/**
 * @param {number} stamp
 * @returns {string}
 */
export const isoStamp = (stamp) => {
  const value = String(stamp);
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
};

const StampLine = (props) => (
  <time datetime={isoStamp(props.stamp)}>{formatStamp(props.stamp)}</time>
);

/**
 * Work log: HEAD posting, tree on demand.
 */
const WorkLog = () => {
  const entries = () => workHistory.data || [];
  const tree = createMemo(() => buildWorkTree(entries()));
  const head = () => latestEntry(entries());
  const [expanded, setExpanded] = createSignal(false);
  const prefersReduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toggle = () => setExpanded(!expanded());

  const EntryLinks = (props) => (
    <Show when={props.links?.length}>
      <WorkLinks>
        <For each={props.links}>
          {(link) => (
            <a href={link.href} target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>
          )}
        </For>
      </WorkLinks>
    </Show>
  );

  return (
    <div data-testid="work-log">
      <Show when={head()}>
        <WorkHead data-testid="work-head">
          <WorkStamp>
            HEAD
            {head().company ? ` · ${head().company}` : ''} · <StampLine stamp={head().stamp} />
          </WorkStamp>
          <WorkTitle>{head().headline || head().title}</WorkTitle>
          <EntryLinks links={head().links} />
          <WorkList>
            <For each={head().bullets}>{(bullet) => <li>{bullet}</li>}</For>
          </WorkList>
          <TreeToggle
            type="button"
            data-testid="work-tree-toggle"
            aria-expanded={expanded()}
            aria-label={expanded() ? A11Y.labels.collapseWork : A11Y.labels.expandWork}
            onClick={toggle}
          >
            {expanded() ? 'collapse tree' : 'open tree'}
          </TreeToggle>
        </WorkHead>
      </Show>
      <Tree
        data-testid="work-tree"
        data-collapsed={expanded() ? 'false' : 'true'}
        style={prefersReduced ? { animation: 'none' } : undefined}
      >
        <For each={tree()}>
          {(node) => (
            <TrunkNode>
              <WorkStamp>
                <StampLine stamp={node.stamp} />
              </WorkStamp>
              <WorkNodeHeading>{node.title}</WorkNodeHeading>
              <EntryLinks links={node.links} />
              <Show when={node.bullets?.length}>
                <WorkList>
                  <For each={node.bullets}>{(bullet) => <li>{bullet}</li>}</For>
                </WorkList>
              </Show>
              <Show when={node.sides?.length}>
                <SideNodes>
                  <For each={[...node.sides].sort((a, b) => b.stamp - a.stamp)}>
                    {(side) => (
                      <SideNode>
                        <SideTitle>
                          {side.title} · <StampLine stamp={side.stamp} />
                        </SideTitle>
                        <EntryLinks links={side.links} />
                        <Show when={side.bullets?.length}>
                          <WorkList>
                            <For each={side.bullets}>{(bullet) => <li>{bullet}</li>}</For>
                          </WorkList>
                        </Show>
                      </SideNode>
                    )}
                  </For>
                </SideNodes>
              </Show>
            </TrunkNode>
          )}
        </For>
      </Tree>
    </div>
  );
};

export default WorkLog;
